/**
 * Tools — a top-level host for system-wide tools (admin). Each tool is a card
 * that opens its own modal/flow. First tool: the Column Renamer.
 */
import { createSignal, Show, For } from 'solid-js';
import { currentUser } from '../lib/auth-store.js';
import ColumnRenamer from '../components/ColumnRenamer.jsx';
import IndexHealthAdvisor from '../components/IndexHealthAdvisor.jsx';
import MockDataDialog from '../components/MockDataDialog.jsx';
import SshBastions from '../components/SshBastions.jsx';
import Connectors from '../components/Connectors.jsx';
import NinocatLink from '../components/NinocatLink.jsx';
import NotificationTargets from '../components/NotificationTargets.jsx';

// Tools are cards grouped by theme. A `soon: true` entry is a planned tool — a
// disabled card with a "Soon" badge, here to give the upcoming feature a home +
// a description. Adding a real tool = drop `soon`, wire its modal below.
const GROUPS = [
  { id: 'schema',    title: 'Schema & structure' },
  { id: 'analytics', title: 'BI & analytics' },
  { id: 'fleet',     title: 'Fleet operations' },
  { id: 'health',    title: 'Health & audit' },
];

const TOOLS = [
  // ── Schema & structure ──────────────────────────────────────────────────
  {
    id: 'column-renamer', group: 'schema', icon: '🔤', title: 'Column Renamer',
    desc: 'Normalise column-name casing across a connection or database (e.g. kodeBrg → KodeBrg), and make the SQL-editor autoformat follow it.',
  },
  {
    id: 'structure-sync', group: 'schema', icon: '🔀', title: 'Structure Synchronization', opensTab: true,
    desc: 'Diff one source-of-truth schema against MANY target connections at once (the fleet matrix) and generate the ALTER / CREATE plan that brings each target in line — per-object same/different/missing, optional mirror (drop extras), preview, then apply with per-statement progress.',
  },
  {
    id: 'table-compare', group: 'schema', icon: '⇄', title: 'Table Comparison', opensTab: true,
    desc: 'Compare per-table record counts and sizes between TWO databases (same connection or two different ones) — side by side with row/size deltas, tables present on only one side flagged. Fast estimate (information_schema) or exact (COUNT *) mode. Read-only.',
  },
  {
    id: 'mock-data', group: 'schema', icon: '🎲', title: 'Mock Data Generator',
    desc: 'Fill a table with fake-but-plausible test rows — a generator inferred per column from its type + name (names, emails, dates, enums, product/SKU/barcode, lat-long, units, …), with per-column overrides. Cascades into empty FK parents in dependency order so foreign keys reference real rows. Preview, then insert directly or generate INSERT SQL.',
  },
  {
    id: 'er-diagram', group: 'schema', icon: '🗺️', title: 'ER Diagram', opensTab: true,
    desc: 'Visualize a database as an entity-relationship diagram — every table as a card (columns, PK/FK, row counts) with foreign-key relationships drawn between them. Auto-laid-out, drag to rearrange (remembered), click a table to focus its relationships, collapse cards, zoom/pan, and export to PNG or SVG.',
  },
  {
    id: 'query-builder', group: 'schema', icon: '🧩', title: 'Visual Query Builder', opensTab: true,
    desc: 'Build a SELECT visually — add tables to a canvas, tick the columns you want, and foreign keys become JOINs automatically. A nested AND/OR filter builder, per-column aggregates (COUNT/SUM/…) with GROUP BY, ORDER BY, DISTINCT and LIMIT drive a live SQL preview you can copy, run inline, or open in a real query tab. "Load SQL" reopens a simple query back into the builder.',
  },
  {
    id: 'schema-designer', group: 'schema', icon: '📐', title: 'Schema Designer', opensTab: true,
    desc: 'Design new tables visually — columns (type, NOT NULL, primary key, AUTO_INCREMENT, default) and foreign keys — and get correct CREATE TABLE DDL live, ordered by foreign-key dependencies. Forward-only: it emits CREATE for new objects (copy it or open in a query tab to run); it never tries to ALTER an existing populated schema.',
  },

  // ── BI & analytics ──────────────────────────────────────────────────────
  {
    id: 'report-builder', group: 'analytics', icon: '📑', title: 'Report Builder', opensTab: true,
    desc: 'Compose saved, runnable, printable reports from ordered blocks — text/headings, SQL queries rendered as tables or charts, and single-number KPI metrics. Report parameters (date range, company id, …) bind into every block as @vars; run with the current values, then print or export to PDF.',
  },

  // ── Fleet operations ────────────────────────────────────────────────────
  {
    id: 'bulk-runner', group: 'fleet', icon: '⚡', title: 'Fleet Query', opensTab: true,
    desc: 'Open a Fleet Query tab — the full query editor (autocomplete, formatting, result grids) where each statement is routed to a connection by prefixing it with [connection name]. One result tab per connection, a fleet-wide message log, and per-connection transactions.',
  },
  {
    id: 'ssh-bastions', group: 'fleet', icon: '🔐', title: 'SSH Bastions',
    desc: 'Reach private MySQL servers through SSH jump hosts. Bastions are first-class shared objects — define once, attach many connections; private keys are AES-GCM-encrypted with the master key (never on disk in plaintext). TOFU host-key verification.',
  },
  {
    id: 'connectors', group: 'fleet', icon: '🛰️', title: 'Connectors',
    // Open to admins AND users with "allow create connections" (others see admin-only tools hidden).
    allow: (u) => u?.role === 'admin' || ((u?.role === 'user' || u?.role === 'subadmin') && !!u?.canCreateConnections),
    desc: 'Reach a MySQL server on a private LAN (behind NAT) with no inbound ports. Download a small agent, run it on the LAN — it dials OUT to Ninocat and stays connected; connections route through it. The reverse of an SSH bastion. The pairing token is baked into the download; online/offline shown live.',
  },
  {
    id: 'ninocat-link', group: 'fleet', icon: '🔗', title: 'Ninocat Link',
    // Admins, and users an admin granted port forwarding.
    allow: (u) => u?.role === 'admin' || !!u?.allowPortForward,
    desc: 'Use your connections from apps on your PC. A small Windows program opens a local port per connection (e.g. 127.0.0.1:13001) and tunnels it through Ninocat to the database — connector, SSH bastion or direct. Apps log in with their own MySQL account.',
  },
  {
    id: 'notification-targets', group: 'fleet', icon: '🔔', title: 'Notification Targets',
    desc: 'Define reusable outbound webhooks (Pushover, Slack, Discord, any HTTP) once — name, URL, headers, body template, and a vault-encrypted token. Scheduled jobs POST them directly or fire them as a notify hook on success/failure. The token is rendered into the request via {{secret}}, never stored in plaintext.',
  },
  {
    id: 'reference-sync', group: 'fleet', icon: '🔗', title: 'Reference Data Sync', opensTab: true,
    desc: 'Push a master reference table (product catalog, region list, fee schedule) from one source database into many same-schema target databases. Matched by primary key; sync the shared columns. Upsert (insert + update, never deletes) or Mirror (also deletes extras). Preview per-target counts, then apply in a transaction.',
  },
  {
    id: 'scheduled-jobs', group: 'fleet', icon: '⏰', title: 'Scheduled Jobs', opensTab: true,
    desc: 'Run unattended jobs on a schedule — database backups (→ .sql), SQL maintenance scripts (single connection or [connection] fleet), or webhook POSTs. Structured schedule (every N / daily / weekly / monthly, no cron), optional notify hook to a vaulted target, per-run history with artifact download, and full audit.',
  },

  // ── Health & audit (read-only) ──────────────────────────────────────────
  {
    id: 'index-advisor', group: 'health', icon: '🔑', title: 'Index & PK Health Advisor',
    desc: 'Find tables with no primary key (replication-unsafe, and what inline editing needs), redundant/duplicate indexes, and unused indexes (8.0+) across a connection. Copy the suggested SQL, or one-click drop the safe ones — the server re-verifies each first.',
  },
  {
    id: 'access-auditor', group: 'health', icon: '🛡️', title: 'Access / Security Auditor', opensTab: true,
    desc: 'Audit MySQL accounts across the fleet for risk — users with no password, anonymous users, root@%, wildcard hosts, and overly-broad global grants — a security review for the data plane. Read-only; degrades gracefully where mysql.user is unreadable.',
  },
  {
    id: 'fleet-health', group: 'health', icon: '📊', title: 'Fleet Health Dashboard', opensTab: true,
    desc: 'One live pane over every connection: version, uptime, total size, biggest tables, connection count, and last-backup time — plus per-connection storage-engine (MyISAM) and charset/collation audits with the offending tables, so the huge / never-backed-up / ancient / mixed-charset DBs surface at a glance.',
  },
  {
    id: 'anomaly-watcher', group: 'health', icon: '🚨', title: 'Data Anomaly Watcher', opensTab: true,
    desc: 'Watch a database on a schedule for suspicious patterns — sudden row-count drops, NULL spikes, duplicate keys, out-of-range or stale values. Each check is a metric SQL + threshold (absolute or relative-to-last-run); a tripped check fires one alert to a notification target. Runs on the scheduler with per-run history.',
  },
  {
    id: 'server-variables', group: 'health', icon: '🔧', title: 'Server Variables & Status', opensTab: true,
    desc: 'View a connection’s raw server settings and runtime counters — SHOW GLOBAL VARIABLES (searchable, editable) and SHOW GLOBAL STATUS (read-only). Edit a dynamic global variable with SET GLOBAL, or — on MySQL 8.0+ — SET PERSIST so it survives a restart without touching my.cnf. Admin-only; every change is audited.',
  },
  {
    id: 'forms-dsl', group: 'health', icon: '🧩', title: 'Forms DSL Workbench', opensTab: true,
    desc: 'Author data-entry modals as a small script (searchable pickers, tables, validation, and the SQL behind them) and preview them live against a connection. Admin-only workbench for the new scriptable Forms/Import engine — still in development.',
  },
  {
    id: 'dependencies', group: 'schema', icon: '🧭', title: 'Dependency Analyzer', opensTab: true,
    desc: 'Find every place an object is used — exhaustively, including deep inside stored procedure/function, trigger, view and event bodies — across one schema or the whole server. Inbound (what references it) + outbound (what it references), plus “where is this database name used?”. Read-only; can be slow server-wide. Also reachable by right-clicking an object or database in the browser.',
  },
  {
    id: 'query-compare', group: 'health', icon: '⚖️', title: 'Query Comparison', opensTab: true,
    desc: 'Run two SELECTs side-by-side to compare performance (interleaved multi-run timing + EXPLAIN + handler-counter deltas) AND verify they return the same result — an order-independent hash of both result sets, in one consistent snapshot. Prove a rewrite is faster and equivalent.',
  },
];

export default function Tools(props) {
  const [openTool, setOpenTool] = createSignal(null);
  const isAdmin = () => currentUser()?.role === 'admin';
  // A tool with no explicit `allow` is admin-only; others (e.g. Connectors) define
  // their own predicate, so non-admin creators see just the tools they may use.
  const toolAllowed = (t) => (t.allow ? t.allow(currentUser()) : isAdmin());
  const toolsIn = (groupId) => TOOLS.filter((t) => t.group === groupId && toolAllowed(t));
  // Most tools open a modal (setOpenTool); a few open a top-level tab instead
  // (opensTab) — those route through a Shell-provided callback, dispatched by id.
  const activate = (t) => {
    if (t.soon) return;
    if (t.opensTab) {
      if (t.id === 'structure-sync') props.onOpenStructureSync?.();
      else if (t.id === 'scheduled-jobs') props.onOpenScheduledJobs?.();
      else if (t.id === 'fleet-health') props.onOpenFleetHealth?.();
      else if (t.id === 'access-auditor') props.onOpenAccessAudit?.();
      else if (t.id === 'query-compare') props.onOpenQueryCompare?.();
      else if (t.id === 'table-compare') props.onOpenTableCompare?.();
      else if (t.id === 'reference-sync') props.onOpenRefSync?.();
      else if (t.id === 'anomaly-watcher') props.onOpenAnomalyWatcher?.();
      else if (t.id === 'er-diagram') props.onOpenERDiagram?.();
      else if (t.id === 'query-builder') props.onOpenQueryBuilder?.();
      else if (t.id === 'schema-designer') props.onOpenSchemaDesigner?.();
      else if (t.id === 'report-builder') props.onOpenReportBuilder?.();
      else if (t.id === 'server-variables') props.onOpenServerVars?.();
      else if (t.id === 'forms-dsl') props.onOpenFormsDsl?.();
      else if (t.id === 'dependencies') props.onOpenDependencies?.();
      else props.onOpenFleetQuery?.();
    } else setOpenTool(t.id);
  };

  return (
    <div class="tools-page">
      <div class="tools-intro">
        <h2>Tools</h2>
        <p class="muted">{isAdmin() ? 'System-wide utilities. Admin only.' : 'Tools available to you.'}</p>
      </div>

      <For each={GROUPS}>
        {(g) => (
          <Show when={toolsIn(g.id).length > 0}>
          <div class="tools-group">
            <h3 class="tools-group-title">{g.title}</h3>
            <div class="tools-grid">
              <For each={toolsIn(g.id)}>
                {(t) => (
                  <button
                    class="tool-card"
                    classList={{ 'tool-card-soon': t.soon }}
                    disabled={t.soon}
                    title={t.soon ? 'Planned — not built yet' : undefined}
                    onClick={() => activate(t)}
                  >
                    <div class="tool-card-icon">{t.icon}</div>
                    <div class="tool-card-title">
                      {t.title}
                      <Show when={t.soon}><span class="tool-card-badge">Soon</span></Show>
                    </div>
                    <div class="tool-card-desc">{t.desc}</div>
                  </button>
                )}
              </For>
            </div>
          </div>
          </Show>
        )}
      </For>

      <Show when={openTool() === 'column-renamer'}>
        <ColumnRenamer onClose={() => setOpenTool(null)} />
      </Show>
      <Show when={openTool() === 'index-advisor'}>
        <IndexHealthAdvisor onClose={() => setOpenTool(null)} />
      </Show>
      <Show when={openTool() === 'mock-data'}>
        <MockDataDialog onClose={() => setOpenTool(null)} />
      </Show>
      <Show when={openTool() === 'ssh-bastions'}>
        <SshBastions onClose={() => setOpenTool(null)} />
      </Show>
      <Show when={openTool() === 'connectors'}>
        <Connectors onClose={() => setOpenTool(null)} />
      </Show>
      <Show when={openTool() === 'ninocat-link'}>
        <NinocatLink onClose={() => setOpenTool(null)} />
      </Show>
      <Show when={openTool() === 'notification-targets'}>
        <NotificationTargets onClose={() => setOpenTool(null)} />
      </Show>
    </div>
  );
}
